import React, { useState } from "react";
import { palette, spacing } from "../../../../styles/theme";
import { Box, FlexColBox } from "../../../../common/components";
import {
  Divider,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSelector } from "../../../../common/hooks/useSelector";
import { useMoment } from "../../../../common/hooks/useMoment";
import { getReducedCartItems } from "../../../../utils/CartUtils";
import { groupBy } from "lodash";
import { FactoryId, ItemPricing, ShippingTier } from "../../../../types";
import { useCurrency } from "../../../../common/hooks/useCurrency";
import CartService from "../../../../service/CartService";
import { useCart } from "../../../../utils/CartHooks";
import { useThemeBreakpoints } from "../../../../utils/themeUtils";
import { DELIVERY_PREP_DAYS_BUFFER } from "../../../../constant/common.constant";
import { sortByShippingOption } from "../../../../utils/SortingUtils";
import { CheckoutStyles } from "../styles";
import CartItemAddtionalOptions from "./CartItemAddtionalOptions";
import RuhmaImpexDeliveryWarning from "../../../../components/RuhmaImpexDeliveryWarning";

const CartItemShipping = () => {
  const { t } = useTranslation();
  const {
    cart: { comboItems },
  } = useSelector((state) => state.cart);
  const { moment, formatFullDate } = useMoment();
  const { isMobile } = useThemeBreakpoints();
  const { FullCurrencyFormatter } = useCurrency();
  const groupedItems = groupBy<ItemPricing>(
    getReducedCartItems(comboItems),
    "factoryId"
  );
  const factoryIds = Object.keys(groupedItems) as FactoryId[];
  const { fetchCartItemsAndUpdateStore } = useCart();

  const [selectedShippingOptionByFactory, setSelectedShippingOptionByFactory] =
    useState<Record<Partial<FactoryId>, ShippingTier>>(
      factoryIds.reduce(
        (acc, factoryId) => {
          acc[factoryId] = groupedItems[factoryId][0].selectedShippingTier;
          return acc;
        },
        {} as Record<Partial<FactoryId>, ShippingTier>
      )
    );

  const handleChangeShippingOption = async (
    factoryId: FactoryId,
    shippingTier: ShippingTier
  ) => {
    setSelectedShippingOptionByFactory((prev) => ({
      ...prev,
      [factoryId]: shippingTier,
    }));
    await CartService.updateShippingMethod(factoryId, shippingTier);
    fetchCartItemsAndUpdateStore();
  };

  return (
    <FlexColBox
      sx={CheckoutStyles.container}
      gap={{ xs: spacing.groupingMobile, lg: spacing.groupingDesktop }}
    >
      {!isMobile && (
        <Typography variant="title">{t("checkout.shipping")}</Typography>
      )}
      {Object.entries(groupedItems).map(([factoryId, items], index) => {
        const shippingOptions = items[0].shippingOptions;

        const selectedShippingTier =
          selectedShippingOptionByFactory[factoryId as FactoryId] ??
          items[0].selectedShippingTier;
        const selectedShippingOption = shippingOptions?.find(
          (option) => option.tier === selectedShippingTier
        );
        const shouldHideDelayAndExpress = items.some((item) => {
          return item.shippingOptions?.some(
            (option) => item.quantity >= option.hideDelayAfter
          );
        });

        return (
          <React.Fragment key={factoryId}>
            <Typography variant="h2">
              {t(
                shouldHideDelayAndExpress
                  ? "checkout.estimated_delivery_without_delay"
                  : "checkout.estimated_delivery",
                {
                  minDate: formatFullDate(
                    moment()
                      .add(selectedShippingOption?.minWeeks, "weeks")
                      .add(DELIVERY_PREP_DAYS_BUFFER, "days")
                  ),
                  maxDate: formatFullDate(
                    moment()
                      .add(selectedShippingOption?.maxWeeks, "weeks")
                      .add(DELIVERY_PREP_DAYS_BUFFER, "days")
                  ),
                }
              )}
            </Typography>
            {factoryId === "RI" && <RuhmaImpexDeliveryWarning />}

            <FlexColBox gap={"12px"}>
              <Typography variant="textRegularBold">
                {t("checkout.delivery_speed")}
              </Typography>
              <RadioGroup sx={{ gap: "12px" }}>
                {shippingOptions
                  ?.sort(sortByShippingOption)
                  .map((shippingOption, index) => {
                    if (shouldHideDelayAndExpress && index > 0) return null;
                    const shippingPrice = items.reduce((acc, curr) => {
                      return (
                        acc +
                        (curr.shippingOptionsCost?.find(
                          (option) => option.tier === shippingOption.tier
                        )?.shippingTotal ?? 0)
                      );
                    }, 0);

                    return (
                      <FormControlLabel
                        key={index}
                        control={
                          <Radio
                            onChange={() =>
                              handleChangeShippingOption(
                                factoryId as FactoryId,
                                shippingOption.tier
                              )
                            }
                            checked={
                              selectedShippingOption?.tier ===
                              shippingOption.tier
                            }
                          />
                        }
                        sx={{
                          "& .MuiFormControlLabel-label": {
                            fontSize: "14px",
                            fontStyle: "normal",
                            fontWeight: "400",
                            lineHeight: "normal",
                            color: palette.grey.nearBlack,
                          },
                        }}
                        label={
                          shouldHideDelayAndExpress
                            ? t(
                                `checkout.delivery_speed_options.shipping_options_without_delay`,
                                {
                                  price:
                                    FullCurrencyFormatter?.format(
                                      shippingPrice
                                    ),
                                  shippingTier: t(
                                    `general.shipping_tier.${shippingOption.tier}`
                                  ),
                                }
                              )
                            : t(
                                `checkout.delivery_speed_options.${shippingOption.tier}`,
                                {
                                  minWeeks: shippingOption.minWeeks,
                                  maxWeeks: shippingOption.maxWeeks,
                                  price:
                                    FullCurrencyFormatter?.format(
                                      shippingPrice
                                    ),
                                }
                              )
                        }
                      />
                    );
                  })}
              </RadioGroup>
            </FlexColBox>
            <FlexColBox gap={"12px"}>
              <Typography variant="textRegularBold">
                {t("general.product", { count: items.length })}
              </Typography>
              <Box
                display={"flex"}
                gap={{
                  xs: spacing.groupingMobile,
                  lg: spacing.groupingDesktop,
                }}
                flexWrap={"wrap"}
              >
                {items.map((item) => (
                  <Box
                    key={item.designId}
                    display={"flex"}
                    alignItems={"center"}
                    height={`82px`}
                    maxWidth={"248px"}
                    width={"100%"}
                    gap={"12px"}
                  >
                    <Box
                      maxWidth={`64px`}
                      height={`64px`}
                      width={"64px"}
                      minWidth={"64px"}
                      p={{ xs: "3px 10px", lg: "3px 10px" }}
                    >
                      <Box
                        component={"img"}
                        sx={{
                          content: `url(${item.productUrls[0]})`,
                          width: "100%",
                          height: "100%",
                          maxHeight: "58px",
                          objectFit: "contain",
                        }}
                      />
                    </Box>
                    <Typography
                      variant="textRegularBold"
                      sx={{
                        textOverflow: "ellipsis",
                        overflow: "hidden",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {item.designName}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </FlexColBox>
            <CartItemAddtionalOptions
              hangTags={items[0].hangTags}
              individualPolyBags={items[0].individualPolyBags}
              factoryId={factoryId as FactoryId}
            />
            {index < Object.keys(groupedItems).length - 1 && <Divider />}
          </React.Fragment>
        );
      })}
    </FlexColBox>
  );
};

export default CartItemShipping;
